// The Image detail tabs (ui-spec §6.11): the digest and what the image is made of, and the build log of an image the
// platform built. Catalog images and bare bases point at an existing image and have no build.
import { formatAbsolute, formatBytes, formatDuration, formatMoney, KeyValueTable, LogViewer, Section } from "@nodus/ui";
import * as React from "react";
import { useLogLines } from "../../app/use-logs";
import { imageSource, shortDigest, stepLabel, type Image, type Scope } from "./model";

type Props = { image: Image; scope: Scope };

function buildSummary(image: Image): string {
  const b = image.status?.build;
  if (!b) return "No build: the image points at one that already exists.";
  if (b.cached) return "Reused the digest of an identical earlier build.";
  if (b.startTime && b.completionTime) return `Built in ${formatDuration(Date.parse(b.completionTime) - Date.parse(b.startTime))}, ${b.cpuSeconds ?? 0} vCPU-seconds${b.costUSD ? `, ${formatMoney(b.costUSD)}` : ""}.`;
  return b.startTime ? `Started ${formatAbsolute(b.startTime)}.` : "Waiting for a builder.";
}

function sourceRow(image: Image): { label: string; value: React.ReactNode; field: string; raw?: string; mono?: boolean } {
  const spec = image.spec ?? {};
  if (spec.fromSandbox) return { label: "Snapshot of", value: spec.fromSandbox.name, field: "spec.fromSandbox", mono: true };
  if (spec.dockerfile) return { label: "Dockerfile", value: spec.dockerfile.inline ? "Inline" : "Uploaded blob", field: "spec.dockerfile" };
  return { label: "Base", value: spec.base ?? "—", field: "spec.base", mono: true, ...(spec.base ? { raw: spec.base } : {}) };
}

export function OverviewTab({ image }: Props) {
  const { spec = {}, status = {} } = image;
  const tags = Object.entries(status.tags ?? {});
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-[var(--nd-section-gap)]">
      <Section title="Image">
        <KeyValueTable
          rows={[
            { label: "Digest", value: status.digest ?? "Not built yet", mono: Boolean(status.digest), field: "status.digest", ...(status.digest ? { raw: status.digest } : {}) },
            { label: "Reference", value: status.reference ?? "—", mono: Boolean(status.reference), field: "status.reference", ...(status.reference ? { raw: status.reference } : {}) },
            {
              label: "Tags",
              value: tags.length ? tags.map(([tag, digest]) => `${tag} → ${shortDigest(digest)}`).join(", ") : (spec.tags ?? []).join(", ") || "—",
              mono: true,
              field: "status.tags",
            },
            { label: "Size", value: status.size ? formatBytes(status.size) : "—", field: "status.size" },
            { label: "Source", value: imageSource(image) },
            sourceRow(image),
            { label: "Platform", value: spec.platform ?? "—", field: "spec.platform" },
            { label: "Python", value: status.python ?? spec.python ?? "—", field: "spec.python" },
            { label: "Build", value: buildSummary(image), field: "status.build" },
          ]}
        />
      </Section>
      {spec.steps?.length ? (
        <Section title="Build steps" count={spec.steps.length}>
          <ol className="grid gap-1 rounded-lg border border-line bg-card p-4">
            {spec.steps.map((step, i) => (
              <li key={i} className="flex gap-3 font-mono text-mono text-fg-secondary">
                <span className="w-6 shrink-0 text-right text-fg-muted tabular-nums">{i + 1}</span>
                <span className="min-w-0 break-words">{stepLabel(step)}</span>
              </li>
            ))}
          </ol>
        </Section>
      ) : null}
    </div>
  );
}

export function BuildTab({ image, scope }: Props) {
  const build = image.status?.build;
  if (!build?.attempt || build.cached) return <p className="text-ui text-fg-muted">{buildSummary(image)}</p>;
  return <BuildLog image={image} scope={scope} attempt={build.attempt} />;
}

function BuildLog({ image, scope, attempt }: Props & { attempt: string }) {
  const { lines, live } = useLogLines("Image", image.metadata.name, scope, { follow: image.status?.phase === "Pending" });
  return <LogViewer lines={lines} live={live} phase={image.status?.phase} provenance={<>Build <code className="font-mono">{attempt}</code></>} />;
}
